import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

// SISIYUYU native variant availability and prefetch 2026-07-17 v11
const sisiVariantSectionCache = new Map();

function normalizeGalleryText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function galleryColorAliases(value) {
  const raw = String(value || "").trim();
  const aliases = new Set([normalizeGalleryText(raw)]);
  const shadeMatch = raw.match(/^(#?\S*\d\S*)\s+(.+)$/);

  if (shadeMatch) {
    const shadeCode = normalizeGalleryText(shadeMatch[1]);
    const description = normalizeGalleryText(shadeMatch[2]);
    aliases.add(description);
    if (shadeCode.length >= 2) aliases.add(shadeCode);

    const descriptionWords = description.split(" ");
    if (descriptionWords.length >= 5) {
      aliases.add(descriptionWords.slice(1).join(" "));
    }
  }

  return [...aliases].filter(Boolean).sort((left, right) => right.length - left.length);
}

function matchGalleryColor(altText, colors) {
  const normalizedAlt = ` ${normalizeGalleryText(altText)} `;
  let bestMatch = null;

  for (const color of colors) {
    for (const alias of galleryColorAliases(color)) {
      if (
        normalizedAlt.includes(` ${alias} `) &&
        (!bestMatch || alias.length > bestMatch.alias.length)
      ) {
        bestMatch = { color, alias };
      }
    }
  }

  return bestMatch?.color || null;
}

function galleryMediaVisibility(items, colors, selectedColor) {
  if (!selectedColor || !colors.length) return items.map(() => true);

  return items.map((item, index) => {
    const matchedColor = matchGalleryColor(item?.alt, colors);
    return index === 0 || !matchedColor || matchedColor === selectedColor;
  });
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.sisiHandleAvailabilityChange.bind(this));
    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('pointerover', this.sisiHandlePrefetch.bind(this), { passive: true });
    this.addEventListener('focusin', this.sisiHandlePrefetch.bind(this));
    this.addEventListener('touchstart', this.sisiHandlePrefetch.bind(this), { passive: true });
    this.sisiFilterProductGallery();
    this.#resizeObserver.observe(this);
    this.sisiUpdateAvailability();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /** SISIYUYU native variant availability and prefetch 2026-07-17 v11 */
  sisiGetVariantMatrix() {
    const script = this.querySelector('script[data-sisi-variant-matrix]');
    if (!script?.textContent) return [];
    try {
      const variants = JSON.parse(script.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch {
      return [];
    }
  }

  sisiSelectAvailableLengthForColor(input) {
    if (!(input instanceof HTMLInputElement)) return false;
    const colorFieldset = input.closest('.sisi-variant-option--color');
    if (!colorFieldset) return false;

    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));
    const colorIndex = fieldsets.indexOf(colorFieldset);
    const lengthFieldset = this.querySelector('.sisi-variant-option--length');
    const lengthIndex = fieldsets.indexOf(lengthFieldset);
    if (colorIndex === -1 || lengthIndex === -1) return false;

    const selectedValues = fieldsets.map(
      (fieldset) => fieldset.querySelector('input:checked')?.value || ''
    );
    const variants = this.sisiGetVariantMatrix().filter(
      (variant) => variant?.available && Array.isArray(variant.options)
    );
    const exactVariant = variants.find(
      (variant) =>
        variant.options.length === selectedValues.length &&
        variant.options.every((value, index) => value === selectedValues[index])
    );
    if (exactVariant) return false;

    for (const lengthInput of lengthFieldset.querySelectorAll('input[type="radio"]')) {
      const candidateValues = [...selectedValues];
      candidateValues[lengthIndex] = lengthInput.value;
      const candidateVariant = variants.find(
        (variant) =>
          variant.options.length === candidateValues.length &&
          variant.options.every((value, index) => value === candidateValues[index])
      );
      if (!candidateVariant) continue;
      lengthInput.checked = true;
      return true;
    }

    return false;
  }

  sisiUpdateAvailability() {
    const matrix = this.sisiGetVariantMatrix().filter((variant) => Array.isArray(variant?.options));
    if (!matrix.length) return;
    const variants = matrix.filter((variant) => variant.available);
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));
    const selectedValues = fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value || '');

    fieldsets.forEach((fieldset, optionIndex) => {
      const compatibleValues = new Set(
        variants
          .filter((variant) =>
            variant.options.every((value, index) => index === optionIndex || !selectedValues[index] || value === selectedValues[index])
          )
          .map((variant) => variant.options[optionIndex])
          .filter(Boolean)
      );
      const globallyAvailableValues = new Set(
        variants.map((variant) => variant.options[optionIndex]).filter(Boolean)
      );
      const keepSellableValuesVisible = fieldset.classList.contains('sisi-variant-option--color');

      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        const label = input.closest('label');
        const enabled = keepSellableValuesVisible ? globallyAvailableValues.has(input.value) : compatibleValues.has(input.value);
        const visible = keepSellableValuesVisible ? globallyAvailableValues.has(input.value) : enabled;
        const serverAriaLabel = input.getAttribute('aria-label') || input.value;
        if (/\s-\sSold out$/i.test(serverAriaLabel)) {
          input.dataset.sisiSoldOutAriaLabel = serverAriaLabel;
        }
        const soldOutAriaLabel = input.dataset.sisiSoldOutAriaLabel || input.value + ' - Sold out';
        input.setAttribute('aria-label', enabled ? input.value : soldOutAriaLabel);
        input.disabled = !enabled;
        input.dataset.optionAvailable = enabled ? 'true' : 'false';
        input.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        if (label) {
          label.hidden = !visible;
          label.setAttribute('aria-hidden', visible ? 'false' : 'true');
          label.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        }
      }
    });
  }

  sisiSetGalleryNodeVisible(node, visible) {
    if (!(node instanceof HTMLElement)) return;
    if (node.dataset.sisiGalleryOriginalHidden === undefined) {
      node.dataset.sisiGalleryOriginalHidden = node.hidden ? 'true' : 'false';
      node.dataset.sisiGalleryOriginalAriaHidden = node.getAttribute('aria-hidden') ?? '__missing__';
      node.dataset.sisiGalleryOriginalDisplay = node.style.getPropertyValue('display');
      node.dataset.sisiGalleryOriginalDisplayPriority = node.style.getPropertyPriority('display') || '__missing__';
    }

    if (visible) {
      node.hidden = node.dataset.sisiGalleryOriginalHidden === 'true';
      const originalAriaHidden = node.dataset.sisiGalleryOriginalAriaHidden;
      if (originalAriaHidden === '__missing__') node.removeAttribute('aria-hidden');
      else node.setAttribute('aria-hidden', originalAriaHidden);
      const originalDisplay = node.dataset.sisiGalleryOriginalDisplay;
      const originalDisplayPriority = node.dataset.sisiGalleryOriginalDisplayPriority;
      if (originalDisplay) {
        node.style.setProperty(
          'display',
          originalDisplay,
          originalDisplayPriority === '__missing__' ? '' : originalDisplayPriority
        );
      } else {
        node.style.removeProperty('display');
      }
      return;
    }

    node.hidden = true;
    node.setAttribute('aria-hidden', 'true');
    node.style.setProperty('display', 'none', 'important');
  }



  sisiFilterProductGallery() {
    if (this.dataset.templateProductMatch !== 'true') return;
    const colorFieldset = this.querySelector('.sisi-variant-option--color');
    const selectedColor = colorFieldset?.querySelector('input:checked')?.value || '';
    const colors = Array.from(
      colorFieldset?.querySelectorAll('input[type="radio"]') || [],
      (input) => input.value
    ).filter(Boolean);
    const gallery = this.closest('.shopify-section')?.querySelector('media-gallery');
    if (!gallery || !selectedColor || !colors.length) return;

    const selectors = [
      'slideshow-slide.product-media-container',
      '.media-gallery__grid > .product-media-container',
      '.dialog-zoomed-gallery > .product-media-container',
      '.slideshow-controls__thumbnail',
      '.dialog-thumbnails-list__thumbnail',
    ];

    const slideshow = gallery.querySelector('slideshow-component');
    const slidesBeforeFilter = Array.from(slideshow?.slides || []);
    const currentSlideIndex = Number.isInteger(slideshow?.current) ? slideshow.current : -1;
    const currentSlide = slidesBeforeFilter[currentSlideIndex];

    for (const selector of selectors) {
      const nodes = Array.from(gallery.querySelectorAll(selector));
      const visibility = galleryMediaVisibility(
        nodes.map((node) => ({ alt: node.querySelector('img')?.alt || '' })),
        colors,
        selectedColor
      );
      nodes.forEach((node, index) => this.sisiSetGalleryNodeVisible(node, visibility[index]));
    }

    const visibleMediaLimit = Number.parseInt(gallery.dataset.sisiVisibleMediaLimit || '0', 10);
    if (Number.isInteger(visibleMediaLimit) && visibleMediaLimit > 0) {
      for (const selector of selectors) {
        const visibleNodes = Array.from(gallery.querySelectorAll(selector)).filter((node) => !node.hidden);
        visibleNodes
          .slice(visibleMediaLimit)
          .forEach((node) => this.sisiSetGalleryNodeVisible(node, false));
      }
    }

    const visibleSlides = Array.from(
      slideshow?.slides || gallery.querySelectorAll('slideshow-slide.product-media-container:not([hidden])')
    );
    const visibleSlideCount = visibleSlides.length;
    let activeVisibleIndex = currentSlide ? visibleSlides.indexOf(currentSlide) : -1;
    if (activeVisibleIndex >= 0) {
      if (slideshow.current !== activeVisibleIndex) slideshow.current = activeVisibleIndex;
    } else if (visibleSlideCount > 0 && typeof slideshow?.select === 'function') {
      let fallbackIndex = -1;
      const originalIndex = currentSlide ? slidesBeforeFilter.indexOf(currentSlide) : currentSlideIndex;
      for (let index = originalIndex - 1; index >= 0; index -= 1) {
        fallbackIndex = visibleSlides.indexOf(slidesBeforeFilter[index]);
        if (fallbackIndex >= 0) break;
      }
      if (fallbackIndex < 0) {
        fallbackIndex = Math.min(Math.max(currentSlideIndex, 0), visibleSlideCount - 1);
      }
      activeVisibleIndex = fallbackIndex;
      slideshow.select(fallbackIndex, undefined, { animate: false });
    }

    for (const counter of gallery.querySelectorAll('.slideshow-controls__counter')) {
      const current = counter.querySelector('[ref="current"]');
      const slash = counter.querySelector('.slash');
      if (!current || !slash) continue;
      const parsedCurrent = Number.parseInt(current.textContent || '', 10);
      const clampedCurrent = visibleSlideCount === 0
        ? 0
        : activeVisibleIndex >= 0
          ? activeVisibleIndex + 1
          : Math.min(Number.isInteger(parsedCurrent) && parsedCurrent > 0 ? parsedCurrent : 1, visibleSlideCount);
      current.textContent = String(clampedCurrent);
      counter.replaceChildren(current, slash, document.createTextNode(String(visibleSlideCount)));
    }
  }

  sisiGetSelectedVariant() {
    const selectedValues = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]')).map(
      (fieldset) => fieldset.querySelector('input:checked')?.value || ''
    );
    if (!selectedValues.length || selectedValues.some((value) => !value)) return null;
    return (
      this.sisiGetVariantMatrix().find(
        (variant) =>
          variant?.available &&
          Array.isArray(variant.options) &&
          variant.options.length === selectedValues.length &&
          variant.options.every((value, index) => value === selectedValues[index])
      ) || null
    );
  }

  sisiApplyInstantVariant() {
    if (this.dataset.templateProductMatch !== 'true') return;
    const variant = this.sisiGetSelectedVariant();
    const section = this.closest('.shopify-section');
    if (!variant || !section) return;

    for (const form of section.querySelectorAll('product-form-component')) {
      if (form.dataset.productId !== this.dataset.productId) continue;
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement) variantIdInput.value = String(variant.id);
      if (variant.featuredImageUrl) {
        form.querySelector('add-to-cart-component')?.setAttribute('data-product-variant-media', variant.featuredImageUrl);
      }
    }

    const gallery = section.querySelector('media-gallery');
    const heroImages = [
      gallery?.querySelector('slideshow-slide.product-media-container img'),
      gallery?.querySelector('.media-gallery__grid > .product-media-container img'),
      gallery?.querySelector('.dialog-zoomed-gallery > .product-media-container img'),
      gallery?.querySelector('.slideshow-controls__thumbnail img'),
      gallery?.querySelector('.dialog-thumbnails-list__thumbnail img'),
    ];
    for (const heroImage of heroImages) {
      if (!(heroImage instanceof HTMLImageElement) || !variant.featuredImageUrl) continue;
      heroImage.src = variant.featuredImageUrl;
      heroImage.removeAttribute('srcset');
      heroImage.removeAttribute('sizes');
      heroImage.alt = variant.options.join(' / ');
    }

    const sticky = section.querySelector('sticky-add-to-cart');
    if (sticky instanceof HTMLElement) {
      sticky.dataset.currentVariantId = String(variant.id);
      const stickyVariant = sticky.querySelector('.sticky-add-to-cart__variant');
      if (stickyVariant) stickyVariant.textContent = variant.options.join(' / ');
      const stickyImage = sticky.querySelector('[ref="productImage"]');
      if (stickyImage instanceof HTMLImageElement && variant.featuredImageUrl) {
        stickyImage.src = variant.featuredImageUrl;
        stickyImage.removeAttribute('srcset');
      }
    }
  }

  sisiBuildPrefetchUrl(input) {
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));
    const targetIndex = Number.parseInt(input.dataset.fieldsetIndex || '', 10);
    const optionValueIds = fieldsets.map((fieldset, index) => {
      const selected = index === targetIndex ? input : fieldset.querySelector('input:checked');
      return selected?.dataset.optionValueId || '';
    });
    if (optionValueIds.some((value) => !value)) return '';
    const section = this.closest('.shopify-section[id]');
    const sectionId = section?.id.replace(/^shopify-section-/, '');
    const productUrl = (this.dataset.productUrl || '').split('?')[0];
    if (!sectionId || !productUrl) return '';
    return productUrl + '?section_id=' + encodeURIComponent(sectionId) + '&option_values=' + optionValueIds.join(',');
  }

  sisiPrefetchInput(input) {
    if (!(input instanceof HTMLInputElement) || input.disabled || input.checked) return;
    const requestUrl = this.sisiBuildPrefetchUrl(input);
    if (!requestUrl || sisiVariantSectionCache.has(requestUrl)) return;
    const requestPromise = fetch(requestUrl)
      .then((response) => {
        if (!response.ok) throw new Error('Variant prefetch failed');
        return response.text();
      })
      .catch((error) => {
        sisiVariantSectionCache.delete(requestUrl);
        throw error;
      });
    sisiVariantSectionCache.set(requestUrl, requestPromise);
    requestPromise.catch(() => {});
  }

  sisiHandlePrefetch(event) {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label');
    const input = label?.querySelector('input[type="radio"]');
    if (input) this.sisiPrefetchInput(input);
  }

  sisiPrefetchVisible() {
    const inputs = Array.from(this.querySelectorAll('fieldset input[type="radio"]'))
      .filter((input) => !input.disabled && !input.checked && !input.closest('label')?.hidden)
      .slice(0, 6);
    inputs.forEach((input, index) => setTimeout(() => this.sisiPrefetchInput(input), index * 120));
  }

  sisiHandleAvailabilityChange(event) {
    if (!(event.target instanceof HTMLInputElement) || !event.target.closest('fieldset')) return;
    this.sisiSelectAvailableLengthForColor(event.target);
    this.sisiUpdateAvailability();
    this.sisiApplyInstantVariant();
    this.sisiFilterProductGallery();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const optionValueId = selectedOption.dataset.optionValueId ?? '';
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, optionValueId);

    const url = new URL(window.location.href);

    const variantId = this.sisiGetSelectedVariant()?.id || selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    const mainSection = this.closest('.shopify-section[id]');
    const mainSectionId = mainSection?.id.replace(/^shopify-section-/, '');
    if (this.dataset.templateProductMatch === 'true' && mainSectionId) {
      params.unshift('section_id=' + encodeURIComponent(mainSectionId));
      productUrl = productUrl?.split('?')[0];
    }
    return productUrl + '?' + params.join('&');
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [optionValueId] - The selected option value ID for event detail.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, optionValueId = '') {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
        },
        promise: deferredEventPromise.promise,
      })
    );

    const cachedResponseText = sisiVariantSectionCache.get(requestUrl);
    let responseTextPromise;
    if (cachedResponseText) {
      responseTextPromise = Promise.resolve(cachedResponseText);
    } else {
      responseTextPromise = fetch(requestUrl, { signal: this.#abortController.signal })
        .then((response) => response.text())
        .catch((error) => {
          sisiVariantSectionCache.delete(requestUrl);
          throw error;
        });
      sisiVariantSectionCache.set(requestUrl, responseTextPromise);
    }

    responseTextPromise.then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();
    this.sisiUpdateAvailability();
    requestAnimationFrame(() => this.sisiFilterProductGallery());

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
